/**
 * `/display/:sessionId` — the same live surface as `/s/:sessionId`, forced read-only, for a spectator screen.
 * Expects spectator credentials for this session; if none are stored, shows a join-as-spectator form using
 * the `?invite=<token>` query param (docs/ARCHITECTURE.md §12).
 */
import { useState, type FormEvent } from 'react';
import { ApiError, joinSession } from '../api.ts';
import { loadCredentials, saveCredentials } from '../session/credentials.ts';
import { SessionSurface } from '../session/SessionPage.tsx';
import { useNavigate, useSearchParams } from '../shell/router.tsx';
import { strings } from '../shell/strings.ts';

export interface DisplayPageProps {
  sessionId: string;
}

export function DisplayPage({ sessionId }: DisplayPageProps) {
  const credentials = loadCredentials(sessionId);

  if (credentials) {
    return <SessionSurface sessionId={sessionId} credentials={credentials} forceReadOnly />;
  }

  return <JoinAsSpectatorForm />;
}

function JoinAsSpectatorForm() {
  const navigate = useNavigate();
  const searchParams = useSearchParams();
  const inviteToken = searchParams.get('invite');
  const [displayName, setDisplayName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!inviteToken) {
    return (
      <div className="page stack">
        <h1>{strings.display.title}</h1>
        <p>{strings.session.noCredentials}</p>
        <footer className="footer">{strings.footer.conceptPrototype}</footer>
      </div>
    );
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const response = await joinSession(inviteToken as string, { displayName, role: 'spectator' });
      saveCredentials(response.sessionId, response.participant);
      navigate(`/display/${response.sessionId}`, { replace: true });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : strings.common.error);
      setSubmitting(false);
    }
  }

  return (
    <div className="page stack">
      <h1>{strings.display.title}</h1>
      <p>{strings.display.joinPrompt}</p>
      <form className="stack" onSubmit={submit}>
        <div className="field">
          <label htmlFor="spectator-name">{strings.join.displayNameLabel}</label>
          <input
            id="spectator-name"
            type="text"
            required
            maxLength={32}
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder={strings.join.displayNamePlaceholder}
          />
        </div>
        {error ? (
          <p className="badge badge-danger" role="alert">
            {error}
          </p>
        ) : null}
        <button type="submit" className="button button-primary" disabled={submitting || !displayName.trim()}>
          {submitting ? strings.join.submitting : strings.display.joinAsSpectator}
        </button>
      </form>
      <footer className="footer">{strings.footer.conceptPrototype}</footer>
    </div>
  );
}
